import { Entitlements } from '@experiences/constants';
import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    type IRelayGroupMetadata,
    PermissionAction,
} from '@experiences/interfaces';
import {
    UiAlertBanner,
    UiCopyButton,
    UiText,
} from '@experiences/ui-common';
import {
    useShowDialog,
    useUserReadableTime,
} from '@experiences/util';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Button } from '@mui/material';
import {
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridHeader,
    ApDataGridHeaderButton,
    ApDataGridRowActions,
    ApDataGridRowButton,
    ApDataGridWrapper,
    ApIconButton,
    ApLink,
    ApTooltip,
} from '@uipath/portal-shell-react';
import React, { useCallback } from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';

import { notificationType } from '../../common/constants/Constant';
import { useDocumentationLinks } from '../../common/hooks/useDocumentationLink';
import { useUiSnackBar } from '../../common/hooks/useUiSnackBar';
import { useBreadcrumbs } from '../../common/providers/BreadcrumbProvider';
import UiPageContainer from '../common/UiPageContainer/UiPageContainer';
import { useEntitlements } from '../entitlements/EntitlementsContextProvider';
import AdminBreadCrumbs from '../organizationsettings/AdminBreadCrumbs';
import useRelaySettingsViewModel from './RelaySettingsViewModel';
import { useHasRelayPermission } from './useRelayPermission';

const RelaySettingsPageComponent: React.FC = () => {
    const { breadcrumbs } = useBreadcrumbs();
    const { formatMessage: translate } = useIntl();
    const getLocalizedLink = useDocumentationLinks();
    const relayAboutDocsSlug = useFeatureFlagValue(Features.RelayAboutDocumentationLink.name, Features.RelayAboutDocumentationLink.defaultValue);
    const documentationLink = getLocalizedLink({ articleSlug: relayAboutDocsSlug as string });
    const { userReadableTime } = useUserReadableTime();
    const { createNotificationWithOptions } = useUiSnackBar();
    const setErrorMessage = useCentralErrorSetter();
    const { getErrorMessage } = useGetErrorInfo();
    const createDialog = useShowDialog();
    const { hasEntitlement } = useEntitlements();
    const {
        tenantId, relayGroups, loading, refresh, onGridChange, pagination, deleteGroup, handleRegisterRelayGroup, onGridRowClick,
    } = useRelaySettingsViewModel();

    const hasRelayCreatePermission = useHasRelayPermission([ PermissionAction.Relay.RelayGroup.Create ], tenantId);
    const hasRelayDeletePermission = useHasRelayPermission([ PermissionAction.Relay.RelayGroup.Delete ], tenantId);
    const canAddRelayGroup = hasEntitlement(Entitlements.Relay) && hasRelayCreatePermission;

    const handleDeleteGroup = useCallback(async (relayGroupId: string, relayGroupName?: string) => {
        const deleteConfirmed = await createDialog({
            title: translate({ id: 'CLIENT_CONFIRM_DELETE_RELAY_GROUP_TITLE' }),
            body: (
                <div>
                    <UiText>
                        {translate(
                            { id: 'CLIENT_CONFIRM_DELETE_RELAY_GROUP_WARNING' },
                            { name: relayGroupName },
                        )}
                    </UiText>
                </div>
            ),
            icon: 'warning',
            showCancel: true,
            primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
        });
        if (deleteConfirmed) {
            try {
                await deleteGroup(relayGroupId);
                createNotificationWithOptions({
                    message: translate(
                        { id: 'CLIENT_RELAY_DELETE_SUCCESS' },
                        { name: relayGroupName },
                    ),
                    type: notificationType.SUCCESS,
                    title: translate({ id: 'CLIENT_RELAY_DELETED' }),
                });
            } catch (error) {
                setErrorMessage(await getErrorMessage(error));
            }
        }
    }, [ deleteGroup, createNotificationWithOptions, setErrorMessage, getErrorMessage, translate, createDialog ]);

    const getUpdatedOnDisplayText = useCallback((relayRegistration: IRelayGroupMetadata) => {
        const updatedDate = relayRegistration.updatedAt;

        if (!updatedDate) {
            return <UiText />;
        }

        const readableTime = userReadableTime(updatedDate);
        return (
            <UiText>
                {readableTime || new Date(updatedDate).toLocaleDateString()}
            </UiText>
        );
    }, [ userReadableTime ]);

    return (
        <UiPageContainer
            position='left'
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadcrumbs} />}
        >
            <UiAlertBanner
                type="info"
                dataCy="relay-settings-description"
            >
                <UiText>
                    <FormattedMessage
                        id='CLIENT_RELAY_SETTINGS_DESCRIPTION'
                        values={{
                            docsLink: (msg: React.ReactNode[]) =>
                                (
                                    <ApLink
                                        href={documentationLink}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        data-cy='relay-documentation-link'
                                    >
                                        {msg}
                                    </ApLink>
                                ),
                        }}
                    />
                </UiText>
            </UiAlertBanner>
            <div
                data-cy="relay-settings-grid-container">
                <ApDataGridWrapper<IRelayGroupMetadata>
                    data={relayGroups?.results ?? []}
                    onChange={onGridChange}
                    selectable={false}
                    loading={loading}
                    refreshable
                    refresh={refresh}
                    dataCy="relay-registrations-grid"
                >
                    <ApDataGridHeader<IRelayGroupMetadata> search>
                        <ApDataGridHeaderButton<IRelayGroupMetadata>
                            id="registerRelay"
                            key="registerRelay"
                            type="main"
                            buttonType="mat-flat-button"
                            color="primary"
                            text={translate({ id: 'CLIENT_REGISTER_RELAY' })}
                            label={translate({ id: 'CLIENT_REGISTER_RELAY' })}
                            onClick={handleRegisterRelayGroup}
                            icon="add"
                            disabled={!canAddRelayGroup}
                            render={() => {
                                const label = translate({ id: 'CLIENT_REGISTER_RELAY' });
                                return (
                                    <ApTooltip
                                        content={canAddRelayGroup
                                            ? label
                                            : translate({ id: 'CLIENT_RELAY_NO_ENTITLEMENT_OR_PERMISSION_TOOLTIP' })}
                                        placement='top'>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<AddIcon />}
                                            disabled={!canAddRelayGroup}
                                            onClick={handleRegisterRelayGroup}
                                            data-cy="register-relay-button"
                                            aria-label={label}
                                        >
                                            {label}
                                        </Button>
                                    </ApTooltip>
                                );
                            }}
                        />
                    </ApDataGridHeader>
                    <ApDataGridColumn<IRelayGroupMetadata>
                        property="name"
                        searchable
                        width="15%"
                        title={translate({ id: 'CLIENT_NAME' })}
                        render={row => (
                            <ApLink
                                data-cy="relay-name-link"
                                onClick={(e) => {
                                    e.stopPropagation(); onGridRowClick(row.id);
                                }}>
                                <UiText>
                                    {row.name}
                                </UiText>
                            </ApLink>
                        )}
                    />

                    <ApDataGridColumn<IRelayGroupMetadata>
                        property="agentCount"
                        width="10%"
                        title={translate({ id: 'CLIENT_RELAY_AGENT_COUNT' })}
                    />
                    <ApDataGridColumn<IRelayGroupMetadata>
                        property="endpointCount"
                        width="10%"
                        title={translate({ id: 'CLIENT_RELAY_ENDPOINT_COUNT' })}
                    />
                    <ApDataGridColumn<IRelayGroupMetadata>
                        property="updatedAt"
                        width="10%"
                        title={translate({ id: 'CLIENT_RELAY_LAST_UPDATED' })}
                        render={getUpdatedOnDisplayText}
                    />
                    <ApDataGridColumn<IRelayGroupMetadata>
                        property="id"
                        width="20%"
                        title={translate({ id: 'CLIENT_RELAY_ID' })}
                    />
                    <ApDataGridRowActions>
                        <ApDataGridRowButton<IRelayGroupMetadata>
                            id='copy-id'
                            dataCy='copy-relay-group-id-button'
                            render={row => <UiCopyButton textToCopy={row.id} />} />
                        <ApDataGridRowButton<IRelayGroupMetadata>
                            id='delete'
                            dataCy='delete-relay-group-button'
                            render={row => {
                                const label = translate({ id: 'CLIENT_DELETE' });
                                return (<ApTooltip content={label}>
                                    <ApIconButton
                                        label={label}
                                        disabled={!hasRelayDeletePermission}
                                        data-cy='delete-button'
                                        color="secondary"
                                        onClick={() => handleDeleteGroup(row.id, row.name)}
                                    >
                                        <DeleteOutlinedIcon aria-label={label} />
                                    </ApIconButton>
                                </ApTooltip>);
                            }}
                        />
                    </ApDataGridRowActions>
                    <ApDataGridFooter
                        pageSizes={[ 10, 25, 50 ]}
                        length={relayGroups?.totalCount ?? 0}
                        pageSize={pagination.top}
                        pageIndex={Math.floor(pagination.skip / pagination.top)}
                    />
                </ApDataGridWrapper>
            </div>
        </UiPageContainer>
    );
};

export default RelaySettingsPageComponent;
